import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { bool, func, shape } from "prop-types";
import Yup from "yup";

import * as i18nUtils from "util/i18n";
import * as schedulerUtils from "util/scheduler";
import {
    BottomSheet,
    Box,
    Drawer,
    Form,
    Separator,
    useMediaQuery,
    transfersTestIds,
} from "@ui-kit/components/index.es";

import LapseSubOptionV3 from "v3/pages/transfers/scheduler/LapseSubOptionV3";

const STARTS_ON_DATE = moment(new Date()).add(1, "days");

function SchedulerDrawerV3(props) {
    SchedulerDrawerV3.propTypes = {
        onScheduleClick: func,
        onClose: func,
        isVisible: bool,
        scheduler: shape({}),
    };

    SchedulerDrawerV3.defaultProps = {
        onScheduleClick: () => {},
        onClose: () => {},
        isVisible: false,
        scheduler: {},
    };

    const isMobile = useMediaQuery("min", "md");
    const { onClose, isVisible, scheduler, onScheduleClick } = props;

    const [values, setValues] = useState({
        startsOn: STARTS_ON_DATE.toDate(),
        lapse: "",
        frequency: "TODAY",
        customFreq: "",
    });

    const [lapseValues, setLapseValues] = useState({
        startsOn: STARTS_ON_DATE.toDate(),
        date: STARTS_ON_DATE.toDate(),
        lapse: schedulerUtils.NEVER,
        number: 1,
    });

    const [formikProps, setFormikProps] = useState({});

    useEffect(() => {
        if (!scheduler) {
            return;
        }

        const newValueDate =
            scheduler.newValueDate && typeof scheduler.newValueDate === "string"
                ? moment(scheduler.newValueDate).toDate()
                : scheduler.newValueDate?.toDate?.() || scheduler.newValueDate;

        const program = scheduler.program || {
            frequency: scheduler.selectedOption,
            startsOn: newValueDate,
        };

        setValues({
            startsOn: program.startsOn || STARTS_ON_DATE.toDate(),
            lapse: program.lapse || "",
            frequency: program.frequency || "TODAY",
            customFreq: "",
        });
        setLapseValues({
            startsOn: program.startsOn || STARTS_ON_DATE.toDate(),
            date: program.date || STARTS_ON_DATE.toDate(),
            lapse: program.lapse || schedulerUtils.NEVER,
            number: program.number || 1,
        });
    }, [isVisible]);

    const { frequency, startsOn } = values;

    const initialValues = useMemo(
        () => ({
            frequency: {
                label: frequency ? i18nUtils.get(`scheduler.${frequency.toLowerCase()}`) : "Ahora",
                value: frequency || "TODAY",
            },
            dateFrom: startsOn,
        }),
        [frequency, startsOn],
    );

    useEffect(() => {
        if (formikProps?.setValues) {
            formikProps.setValues({
                frequency: {
                    label: frequency ? i18nUtils.get(`scheduler.${frequency.toLowerCase()}`) : "Ahora",
                    value: frequency || "TODAY",
                },
                dateFrom: startsOn,
            });
        }
    }, [formikProps, frequency, startsOn]);

    const handleOnScheduleClick = useCallback(
        (schedulerFormValues) => {
            onScheduleClick({ ...schedulerFormValues, ...values, lapse: { ...lapseValues } });
        },
        [lapseValues, onScheduleClick, values],
    );

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                dateFrom: Yup.string()
                    .nullable()
                    .test("test", i18nUtils.get("scheduler.startsOn.greaterThanEndDate"), (date) => {
                        if (
                            frequency === schedulerUtils.TODAY ||
                            frequency === schedulerUtils.ONCE ||
                            lapseValues.lapse === schedulerUtils.NEVER ||
                            lapseValues.lapse === schedulerUtils.UP_TO
                        ) {
                            return true;
                        }
                        if (lapseValues.lapse !== schedulerUtils.UNTIL) {
                            return true;
                        }
                        const endDate = lapseValues.date !== undefined ? lapseValues.date : new Date(date);
                        return new Date(date) <= endDate;
                    }),
            }),
        [frequency, lapseValues.date, lapseValues.lapse],
    );

    const inputsList = useMemo(
        () => [
            {
                colProps: { size: { xs: 12 } },
                component: "selectFieldV3",
                id: "frequency",
                "data-testid": transfersTestIds.scheduler.frequency,
                label: i18nUtils.get("scheduler.frequency.label"),
                required: true,
                variant: "dark",
                options: schedulerUtils.listRecurrencies(),
                onSelectOption: (freq) => setValues((prev) => ({ ...prev, frequency: freq.value })),
            },
            ...(frequency !== schedulerUtils.TODAY
                ? [
                      {
                          colProps: { size: { xs: 12 } },
                          component: "dateFieldV3",
                          id: "dateFrom",
                          "data-testid": transfersTestIds.scheduler.startsOn,
                          label: i18nUtils.get("scheduler.startsOn.label"),
                          minDate: STARTS_ON_DATE.toDate(),
                          startDate: startsOn,
                          formattedDisplay: true,
                          selectsStart: true,
                          variant: "dark",
                          onDateChange: (date) => setValues((prev) => ({ ...prev, startsOn: date })),
                      },
                  ]
                : []),
            ...(frequency !== schedulerUtils.TODAY && frequency !== schedulerUtils.ONCE
                ? [
                      {
                          colProps: { size: { xs: 12 } },
                          id: "lapse",
                          customElement: () => (
                              <Box w="100%">
                                  <Separator type="horizontal" color="neutral-superlight" m={0} />
                                  <LapseSubOptionV3
                                      values={lapseValues}
                                      onChange={setLapseValues}
                                      frequency={frequency}
                                  />
                              </Box>
                          ),
                      },
                  ]
                : []),
        ],
        [frequency, lapseValues, startsOn],
    );

    const renderForm = () => (
        <Box px={isMobile ? 16 : 0} column gap={24} pb={24}>
            <Form
                buttonSubmit={{}}
                disabledSubmitByDirty
                rowSpacingForm={24}
                withSeparator={false}
                wrapButtons
                initialValues={initialValues}
                inputsList={inputsList}
                buttonsList={[
                    {
                        colProps: { size: { xs: 12 } },
                        component: "button",
                        key: "schedule",
                        "data-testid": transfersTestIds.scheduler.submit,
                        text: i18nUtils.get("scheduler.schedule"),
                        icon: "Check",
                        iconVariant: "outline",
                        variant: "solid",
                        type: "submit",
                        rounded: true,
                        withShadow: true,
                        onClick: () => {},
                    },
                ]}
                onSubmit={handleOnScheduleClick}
                setFormikProps={setFormikProps}
                validationSchema={validationSchema}
                className="w-100"
            />
        </Box>
    );

    if (isMobile) {
        return (
            <BottomSheet
                data-testid={transfersTestIds.scheduler.drawer}
                title={i18nUtils.get("scheduler.title")}
                iconTitle="CalendarAdd"
                onClose={onClose}
                isVisible={isVisible}
                renderWithPortal>
                {renderForm()}
            </BottomSheet>
        );
    }

    return (
        <Drawer
            data-testid={transfersTestIds.scheduler.drawer}
            keepFixedOnWeb={false}
            iconTitle="CalendarAdd"
            onClose={onClose}
            placement="right"
            isVisible={isVisible}
            title={i18nUtils.get("scheduler.title")}>
            {renderForm()}
        </Drawer>
    );
}

export default SchedulerDrawerV3;
